import type { Actor, Annotation, ImportResult, WebRemarqOptions } from './core/types'
import { AnnotationStorage } from './core/storage'
import { LocalStorageAdapter } from './core/local-storage-adapter'
import { createFingerprint } from './core/fingerprint'
import { matchElement } from './core/matcher'
import { generateAgentExport } from './core/agent-export'
import { transition, type LifecycleAction } from './core/lifecycle'
import { injectStyles, removeStyles } from './ui/styles'
import { ThemeManager } from './ui/theme'
import { Toolbar } from './ui/toolbar'
import { Overlay } from './ui/overlay'
import { SpacingOverlay } from './ui/spacing-overlay'
import { Popup } from './ui/popup'
import { MarkerManager } from './ui/markers'
import { DetachedPanel } from './ui/detached-panel'
import { showToast, hideToast } from './ui/toast'
import { showShortcutsModal, hideShortcutsModal } from './ui/shortcuts-modal'
import { RouteObserver } from './spa'
import { toBucket, initViewportListener, destroyViewportListener } from './core/viewport'

let initialized = false
let options: WebRemarqOptions = {}
let storage: AnnotationStorage
let themeManager: ThemeManager
let toolbar: Toolbar
let overlay: Overlay
let popup: Popup
let markers: MarkerManager
let detachedPanel: DetachedPanel
let routeObserver: RouteObserver
let inspecting = false
let spacingMode = false
let spacingOverlay: SpacingOverlay
let mutationObserver: MutationObserver | null = null
let unsubRoute: (() => void) | null = null
let refreshScheduled = false
let savedCursor = ''

// WeakRef cache: annotation id → element (survives GC of element)
const elementCache = new Map<string, WeakRef<HTMLElement>>()

function describeTarget(el: HTMLElement): string {
  const parts: string[] = []

  // id
  if (el.id) parts.push(`#${el.id}`)

  // data attributes
  const dataAnnotate = el.getAttribute(options.dataAttribute ?? 'data-annotate')
  const dataTestId = el.getAttribute('data-testid') || el.getAttribute('data-test') || el.getAttribute('data-cy')
  if (dataAnnotate) parts.push(`[${dataAnnotate}]`)
  else if (dataTestId) parts.push(`[${dataTestId}]`)

  // Meaningful classes (max 2)
  const classes = Array.from(el.classList)
    .filter((c) => !c.match(/^(sc-|css-)/) && !c.match(/^[a-zA-Z0-9]{8,}$/) && !c.match(/__[a-zA-Z0-9]{3,}$/))
    .slice(0, 2)
  if (classes.length) parts.push(`.${classes.join('.')}`)

  // Direct text only
  let text = ''
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) text += node.textContent ?? ''
  }
  text = text.trim()
  if (!text && el.children.length <= 2) text = el.textContent?.trim() ?? ''
  if (text) parts.push(`"${text.slice(0, 30)}"`)

  return parts.join(' ') || ''
}

function currentRoute(): string {
  return location.pathname + location.hash
}

function generateId(): string {
  return `ann-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function cacheElement(annotationId: string, el: HTMLElement): void {
  elementCache.set(annotationId, new WeakRef(el))
}

function getCachedElement(annotationId: string): HTMLElement | null {
  const ref = elementCache.get(annotationId)
  if (!ref) return null
  const el = ref.deref()
  if (!el || !el.isConnected) {
    elementCache.delete(annotationId)
    return null
  }
  return el
}

function resolveElement(ann: Annotation): HTMLElement | null {
  // 1. Check cache first
  const cached = getCachedElement(ann.id)
  if (cached) return cached

  // 2. Fall back to fingerprint matching
  const el = matchElement(ann.fingerprint, { dataAttribute: options.dataAttribute })
  if (el) {
    cacheElement(ann.id, el)
    console.debug(`[web-remarq] Matched "${ann.comment}" via fingerprint on <${el.tagName.toLowerCase()}>`)
  } else {
    console.debug(`[web-remarq] Could not match "${ann.comment}"`, ann.fingerprint)
  }
  return el
}

function refreshMarkers(): void {
  markers.clear()
  const attached: { ann: Annotation; el: HTMLElement }[] = []
  const otherBreakpoint: Annotation[] = []
  const detached: Annotation[] = []
  const route = currentRoute()
  const anns = storage.getByRoute(route)
  const bucket = toBucket(window.innerWidth)  // always read fresh

  for (const ann of anns) {
    const el = resolveElement(ann)
    if (el) {
      attached.push({ ann, el })
    } else if (bucket !== ann.viewportBucket) {
      otherBreakpoint.push(ann)
    } else {
      detached.push(ann)
    }
  }

  for (const { ann, el } of attached) {
    markers.addMarker(ann, el)
  }

  detachedPanel.update(otherBreakpoint, detached)

  const needsAttention = anns.filter(
    (a) => a.status === 'pending' || a.status === 'in_progress',
  ).length
  const needsVerification = anns.filter((a) => a.status === 'fixed_unverified').length
  toolbar.setBadgeCount(needsAttention)
  toolbar.setVerificationBadgeCount(needsVerification)
}

function jumpToFirstUnverified(): void {
  if (!storage || !markers) return
  const ann = storage.getByRoute(currentRoute()).find((a) => a.status === 'fixed_unverified')
  if (!ann) return
  markers.scrollToMarker(ann.id)
}

// Debounced refresh — MutationObserver can fire rapidly
function scheduleRefresh(): void {
  if (refreshScheduled) return
  refreshScheduled = true
  requestAnimationFrame(() => {
    refreshScheduled = false
    refreshMarkers()
  })
}

function handleInspectClick(e: MouseEvent): void {
  if (!inspecting) return

  const target = e.target as HTMLElement
  if (!target || target.closest('[data-remarq-theme]')) return

  e.preventDefault()
  e.stopPropagation()

  setInspecting(false)

  const rect = target.getBoundingClientRect()
  popup.show(
    {
      tag: target.tagName.toLowerCase(),
      text: describeTarget(target),
    },
    {
      top: window.scrollY + rect.bottom + 8,
      left: window.scrollX + rect.left,
      anchorBottom: window.scrollY + rect.top - 8,
    },
    (comment) => {
      const fp = createFingerprint(target, {
        classFilter: options.classFilter,
        dataAttribute: options.dataAttribute,
      })
      const now = Date.now()
      const ann: Annotation = {
        id: generateId(),
        comment,
        fingerprint: fp,
        route: currentRoute(),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        viewportBucket: toBucket(window.innerWidth),
        timestamp: now,
        status: 'pending',
        lifecycle: [{ type: 'created', actor: 'designer', timestamp: now }],
      }
      // Cache the element immediately — no need to re-match
      cacheElement(ann.id, target)
      storage.add(ann)
      refreshMarkers()
      showToast(themeManager.container, 'Annotation added')
    },
    () => {
      // cancel
    },
  )
}

function handleInspectHover(e: MouseEvent): void {
  if (!inspecting) return
  const target = e.target as HTMLElement
  if (!target || target.closest('[data-remarq-theme]')) return

  if (spacingMode) {
    overlay.show(target)
    overlay.hideHighlight()
    spacingOverlay.show(target)
  } else {
    overlay.show(target)
  }
  overlay.updateTooltipPosition(e.clientX, e.clientY)
}

function handleInspectKeydown(e: KeyboardEvent): void {
  if (options.shortcuts === false) return
  // Ignore when typing in inputs
  const tag = (e.target as HTMLElement)?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return

  if (e.key === 'Escape' && inspecting) {
    setInspecting(false)
    overlay.hide()
    spacingOverlay.hide()
  }

  if (e.key === 's' && inspecting) {
    spacingMode = !spacingMode
    toolbar.setSpacingActive(spacingMode)
    if (!spacingMode) spacingOverlay.hide()
  }

  if (e.altKey && e.code === 'KeyI') {
    e.preventDefault()
    setInspecting(!inspecting)
    if (!inspecting) {
      overlay.hide()
      spacingOverlay.hide()
    }
  }

  if (e.altKey && e.code === 'KeyC') {
    e.preventDefault()
    copyToClipboard()
  }

  if (e.altKey && e.code === 'KeyD') {
    e.preventDefault()
    elementCache.clear()
    storage.clearAll()
    refreshMarkers()
    showToast(themeManager.container, 'All annotations cleared')
  }

  if (e.key === '?') {
    showShortcutsModal(themeManager.container)
  }
}

function setInspecting(value: boolean): void {
  if (value && !inspecting) {
    savedCursor = document.body.style.cursor
    document.body.style.cursor = 'crosshair'
  }
  if (!value && inspecting) {
    document.body.style.cursor = savedCursor
  }
  inspecting = value
  toolbar.setInspectActive(value)
  toolbar.setSpacingEnabled(value)
  if (!value) {
    overlay.hide()
    spacingOverlay?.hide()
    spacingMode = false
    toolbar.setSpacingActive(false)
  }
}

function handleMarkerClick(annotationId: string): void {
  const ann = storage.getAll().find((a) => a.id === annotationId)
  if (!ann) return

  const el = resolveElement(ann)
  if (!el) return

  const rect = el.getBoundingClientRect()

  popup.showDetail(
    {
      tag: ann.fingerprint.tagName,
      text: ann.fingerprint.textContent ?? '',
      comment: ann.comment,
      status: ann.status,
      lifecycle: ann.lifecycle,
    },
    {
      top: window.scrollY + rect.bottom + 8,
      left: window.scrollX + rect.left,
      anchorBottom: window.scrollY + rect.top - 8,
    },
    {
      onTransition: (action, reason) => {
        applyTransition(ann.id, action, reason ? { reason } : undefined)
      },
      onDelete: () => {
        elementCache.delete(ann.id)
        storage.remove(ann.id)
        refreshMarkers()
      },
      onClose: () => {},
      onEdit: (newComment: string) => {
        storage.update(ann.id, { comment: newComment })
        refreshMarkers()
      },
      onCopy: () => {
        const fresh = storage.getById(ann.id) ?? ann
        const fp = fresh.fingerprint
        const lines = [
          `[${fresh.status}] "${fresh.comment}"`,
          `Element: <${fp.tagName}>${fp.textContent ? ` "${fp.textContent}"` : ''}`,
          `Route: ${fresh.route}`,
          `Viewport: ${fresh.viewportBucket}px`,
        ]
        if (fp.sourceLocation) lines.push(`Source: ${fp.sourceLocation}`)
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
          showToast(themeManager.container, 'Annotation copied')
        }).catch(() => {
          console.warn('[web-remarq] Clipboard write failed')
        })
      },
    },
  )
}

function generateMarkdown(): string {
  const route = currentRoute()
  const anns = storage.getByRoute(route)
  if (!anns.length) return ''

  const lines = [`## Annotations — ${route} (${anns.length})`, '']

  anns.forEach((ann, i) => {
    const fp = ann.fingerprint

    lines.push(`### ${i + 1}. [${ann.status}] "${ann.comment}"`)

    let elDesc = `Element: <${fp.tagName}>`
    if (fp.textContent) elDesc += ` "${fp.textContent}"`
    lines.push(elDesc)
    lines.push(`Viewport: ${ann.viewportBucket}px`)
    lines.push('')

    if (fp.sourceLocation) {
      lines.push(`Source: \`${fp.sourceLocation}\`${fp.componentName ? ` (${fp.componentName})` : ''}`)
      lines.push('')
    } else if (fp.detectedSource) {
      lines.push(`Source (detected): \`${fp.detectedSource}\`${fp.detectedComponent ? ` (${fp.detectedComponent})` : ''}`)
      lines.push('')
    }

    lines.push('Search hints:')

    if (fp.dataAnnotate) {
      lines.push(`- \`data-annotate="${fp.dataAnnotate}"\` — in template files`)
    }
    if (fp.dataTestId) {
      lines.push(`- \`data-testid="${fp.dataTestId}"\` — in template files`)
    }
    if (fp.id) {
      lines.push(`- \`id="${fp.id}"\` — in template files`)
    }
    if (fp.ariaLabel) {
      lines.push(`- \`aria-label="${fp.ariaLabel}"\` — in template files`)
    }
    if (fp.textContent) {
      lines.push(`- \`"${fp.textContent}"\` — text content in templates`)
    }
    if (fp.cssModules?.length) {
      for (const mod of fp.cssModules) {
        lines.push(`- \`.${mod.localName}\` — in CSS Module file (likely \`${mod.moduleHint}.module.*\`)`)
        lines.push(`- \`styles.${mod.localName}\` — in component JS/TS`)
      }
    }
    if (fp.domPath) {
      lines.push(`- DOM: ${fp.domPath}`)
    }
    const classes = fp.rawClasses ?? fp.stableClasses
    if (classes.length) {
      lines.push(`- Classes: ${classes.join(' ')}`)
    }

    lines.push('')
  })

  return lines.join('\n')
}

function downloadFile(content: string, filename: string, type: string): void {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function exportMarkdown(): void {
  const md = generateMarkdown()
  if (!md) return
  downloadFile(md, `remarq-annotations-${Date.now()}.md`, 'text/markdown')
  showToast(themeManager.container, 'Exported as Markdown')
}

function exportJSON(): void {
  const data = storage.exportJSON()
  const json = JSON.stringify(data, null, 2)
  downloadFile(json, `remarq-annotations-${Date.now()}.json`, 'application/json')
  showToast(themeManager.container, 'Exported as JSON')
}

function copyToClipboard(): void {
  const md = generateMarkdown()
  if (!md) {
    showToast(themeManager.container, 'No annotations to copy')
    return
  }
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(md).then(() => {
      showToast(themeManager.container, 'Copied to clipboard')
    }).catch(() => {
      fallbackCopy(md)
    })
  } else {
    fallbackCopy(md)
  }
}

function fallbackCopy(text: string): void {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    document.execCommand('copy')
    showToast(themeManager.container, 'Copied to clipboard')
  } catch {
    showToast(themeManager.container, 'Failed to copy')
  }
  textarea.remove()
}

function exportAgent(): void {
  const anns = storage.getAll()
  if (!anns.length) return
  const data = generateAgentExport(anns, toBucket(window.innerWidth))
  const json = JSON.stringify(data, null, 2)
  downloadFile(json, `remarq-agent-${Date.now()}.json`, 'application/json')
}

function copyAgentToClipboard(): void {
  const anns = storage.getAll()
  if (!anns.length) return
  const data = generateAgentExport(anns, toBucket(window.innerWidth))
  const json = JSON.stringify(data, null, 2)
  navigator.clipboard.writeText(json).catch(() => {
    console.warn('[web-remarq] Clipboard write failed')
  })
}

interface TransitionOpts {
  actor?: Actor
  actorName?: string
  reason?: string
}

function applyTransition(id: string, action: LifecycleAction, opts: TransitionOpts = {}): void {
  if (!storage) return
  const ann = storage.getById(id)
  if (!ann) return
  const { status, event } = transition(ann, action, opts)
  const lifecycle = [...ann.lifecycle, event]
  storage.update(id, { status, lifecycle })
  markers?.updateStatus(id, status)
  refreshMarkers()
}

function setupMutationObserver(): void {
  mutationObserver = new MutationObserver((mutations) => {
    let hasExternalMutation = false
    for (const m of mutations) {
      if (m.target instanceof HTMLElement && m.target.closest('[data-remarq-theme]')) continue
      hasExternalMutation = true
      break
    }
    if (hasExternalMutation) scheduleRefresh()
  })

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['id', 'class', 'data-annotate', 'data-testid', 'data-test', 'data-cy'],
  })
}

export const WebRemarq = {
  init(opts?: WebRemarqOptions): void {
    if (initialized) return
    if (!document.body) {
      document.addEventListener('DOMContentLoaded', () => WebRemarq.init(opts), { once: true })
      return
    }
    options = opts ?? {}

    try {
      injectStyles()
      storage = new AnnotationStorage(options.storage ?? new LocalStorageAdapter())
      themeManager = new ThemeManager(document.body, options.theme)
      overlay = new Overlay(themeManager.container)
      spacingOverlay = new SpacingOverlay(themeManager.container)
      popup = new Popup(themeManager.container)
      markers = new MarkerManager(themeManager.container, handleMarkerClick)
      const position = options.position ?? 'bottom-right'

      detachedPanel = new DetachedPanel(themeManager.container, (id) => {
        elementCache.delete(id)
        storage.remove(id)
        refreshMarkers()
      }, position)

      toolbar = new Toolbar(themeManager.container, {
        onInspect: () => setInspecting(!inspecting),
        onSpacingToggle: () => {
          if (!inspecting) return
          spacingMode = !spacingMode
          toolbar.setSpacingActive(spacingMode)
          if (!spacingMode) spacingOverlay.hide()
        },
        onCopy: copyToClipboard,
        onExportMd: exportMarkdown,
        onExportJson: exportJSON,
        onImport: () => {
          const file = toolbar.getFileInput().files?.[0]
          if (file) {
            WebRemarq.import(file)
          }
        },
        onClear: () => {
          elementCache.clear()
          storage.clearAll()
          refreshMarkers()
          showToast(themeManager.container, 'All annotations cleared')
        },
        onThemeToggle: () => themeManager.toggle(),
        onHelp: () => showShortcutsModal(themeManager.container),
        onVerificationBadgeClick: jumpToFirstUnverified,
      }, position)

      routeObserver = new RouteObserver()
      unsubRoute = routeObserver.onChange(() => refreshMarkers())

      document.addEventListener('click', handleInspectClick, true)
      document.addEventListener('mousemove', handleInspectHover)
      document.addEventListener('keydown', handleInspectKeydown)

      setupMutationObserver()
      initViewportListener(() => refreshMarkers())

      storage.ready.then(() => {
        if (storage.isMemoryOnly) {
          toolbar.setMemoryWarning(true)
        }
        refreshMarkers()
      })

      console.debug(`[web-remarq] Initialized on route: ${currentRoute()}`)
      initialized = true
    } catch (err) {
      console.error('[web-remarq] Init failed:', err)
    }
  },

  destroy(): void {
    if (!initialized) return
    try {
      document.removeEventListener('click', handleInspectClick, true)
      document.removeEventListener('mousemove', handleInspectHover)
      document.removeEventListener('keydown', handleInspectKeydown)
      mutationObserver?.disconnect()
      mutationObserver = null
      if (inspecting) {
        document.body.style.cursor = savedCursor
      }
      hideToast()
      hideShortcutsModal()
      destroyViewportListener()
      unsubRoute?.()
      routeObserver?.destroy()
      markers?.destroy()
      detachedPanel?.destroy()
      popup?.destroy()
      overlay?.destroy()
      spacingOverlay?.destroy()
      toolbar?.destroy()
      themeManager?.destroy()
      removeStyles()
      elementCache.clear()
      inspecting = false
      spacingMode = false
      initialized = false
    } catch (err) {
      console.error('[web-remarq] Destroy failed:', err)
    }
  },

  setTheme(theme: 'light' | 'dark'): void {
    themeManager?.setTheme(theme)
  },

  export(format: 'md' | 'json' | 'agent'): void {
    if (format === 'md') exportMarkdown()
    else if (format === 'json') exportJSON()
    else exportAgent()
  },

  copy(format?: 'md' | 'agent'): void {
    if (format === 'agent') copyAgentToClipboard()
    else copyToClipboard()
  },

  async import(file: File): Promise<ImportResult> {
    const text = await file.text()
    const data = JSON.parse(text)
    storage.importJSON(data)
    refreshMarkers()

    const allAnns = storage.getAll()
    const bucket = toBucket(window.innerWidth)
    let matched = 0
    let otherBreakpoint = 0
    let detached = 0
    for (const ann of allAnns) {
      if (resolveElement(ann)) {
        matched++
      } else if (bucket !== ann.viewportBucket) {
        otherBreakpoint++
      } else {
        detached++
      }
    }
    return { total: allAnns.length, matched, otherBreakpoint, detached }
  },

  getAnnotations(route?: string): Annotation[] {
    if (!storage) return []
    return route ? storage.getByRoute(route) : storage.getAll()
  },

  clearAll(): void {
    elementCache.clear()
    storage?.clearAll()
    if (initialized) refreshMarkers()
  },

  acknowledge(id: string, opts?: TransitionOpts): void {
    applyTransition(id, 'acknowledge', opts)
  },

  claimFix(id: string, opts?: TransitionOpts): void {
    applyTransition(id, 'claimFix', opts)
  },

  verify(id: string, opts?: TransitionOpts): void {
    applyTransition(id, 'verify', opts)
  },

  reject(id: string, opts?: TransitionOpts): void {
    applyTransition(id, 'reject', opts)
  },

  dismiss(id: string, opts?: TransitionOpts): void {
    applyTransition(id, 'dismiss', opts)
  },

  reopen(id: string, opts?: TransitionOpts): void {
    applyTransition(id, 'reopen', opts)
  },

  /** @deprecated Use verify() instead. */
  markResolved(id: string): void {
    applyTransition(id, 'verify')
  },
}
